import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import { RoleBadge } from '../common/RoleBadge';
import * as adminService from '../../services/adminService';
import { ResetPasswordDialog } from './ResetPasswordDialog';

type PermSortKey = 'entity' | 'read' | 'write' | 'delete' | 'execute' | 'admin';
type SortDir = 'asc' | 'desc';

function parsePermission(perm: string): { entity: string; action: string } | null {
  const dot = perm.indexOf('.');
  if (dot < 0) return null;
  return { entity: perm.substring(0, dot), action: perm.substring(dot + 1) };
}

interface UserProfile {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  role_name?: string;
  role?: string;
  auth_provider?: string;
  created_at: string;
  last_login?: string;
  avatar_url?: string;
  permissions?: string[];
}

interface ActivityEntry {
  id: string;
  action: string;
  detail?: string;
  timestamp: string;
  ip_address?: string;
}

interface EntityCount {
  entity_type: string;
  count: number;
}

interface UserDetailPanelProps {
  userId: string;
  onClose: () => void;
}

type Tab = 'activity' | 'entities' | 'permissions';

export const UserDetailPanel: React.FC<UserDetailPanelProps> = ({ userId, onClose }) => {
  const { darkMode } = useTheme();
  const { showToast } = useToast();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<Tab>('activity');

  // Activity tab
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Entities tab
  const [entities, setEntities] = useState<EntityCount[]>([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);

  // Permissions tab
  const [permSortKey, setPermSortKey] = useState<PermSortKey>('entity');
  const [permSortDir, setPermSortDir] = useState<SortDir>('asc');

  // Role change
  const [changingRole, setChangingRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);

  // Reset password
  const [showResetDialog, setShowResetDialog] = useState(false);

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await adminService.getUserProfile(userId);
        setProfile(data);
        setSelectedRole(data.role_name || data.role || 'viewer');
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load user profile');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId]);

  // Fetch activity when tab selected
  useEffect(() => {
    if (activeTab !== 'activity') return;
    const fetchActivity = async () => {
      setActivityLoading(true);
      try {
        const data = await adminService.getUserActivity(userId);
        setActivity(Array.isArray(data) ? data : data.items || []);
      } catch (err) {
        console.error('Failed to load activity:', err);
      } finally {
        setActivityLoading(false);
      }
    };
    fetchActivity();
  }, [userId, activeTab]);

  // Fetch entities when tab selected
  useEffect(() => {
    if (activeTab !== 'entities') return;
    const fetchEntities = async () => {
      setEntitiesLoading(true);
      try {
        const data = await adminService.getUserEntities(userId);
        // API returns {counts: {personas: 5, ...}} — transform to EntityCount[]
        if (Array.isArray(data)) {
          setEntities(data);
        } else if (data.counts) {
          setEntities(Object.entries(data.counts).map(([k, v]) => ({ entity_type: k, count: v as number })));
        } else {
          setEntities([]);
        }
      } catch (err) {
        console.error('Failed to load entities:', err);
      } finally {
        setEntitiesLoading(false);
      }
    };
    fetchEntities();
  }, [userId, activeTab]);

  const handleRoleChange = async () => {
    if (!selectedRole || selectedRole === (profile?.role_name || profile?.role)) {
      setShowRoleDropdown(false);
      return;
    }
    setChangingRole(true);
    try {
      await adminService.assignRole(userId, selectedRole);
      showToast(`Role changed to ${selectedRole}`, 'success');
      setProfile((prev) => prev ? { ...prev, role_name: selectedRole } : prev);
      setShowRoleDropdown(false);
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Failed to change role', 'error');
    } finally {
      setChangingRole(false);
    }
  };

  const getInitials = (p: UserProfile): string => {
    if (p.full_name) {
      return p.full_name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
    return (p.username || p.email).substring(0, 2).toUpperCase();
  };

  if (loading) {
    return (
      <div className={`rounded-lg shadow-lg p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className={`rounded-lg shadow-lg p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>User Detail</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-4 rounded-md bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-100">
          {error || 'Failed to load user profile'}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      {/* Header */}
      <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            {profile.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt={profile.full_name || profile.username}
                className="w-14 h-14 rounded-full object-cover"
              />
            ) : (
              <div
                className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-medium ${
                  darkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'
                }`}
              >
                {getInitials(profile)}
              </div>
            )}
            <div>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                {profile.full_name || profile.username}
              </h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{profile.email}</p>
              <div className="mt-1">
                <RoleBadge role={profile.role_name || profile.role || 'viewer'} />
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Meta info */}
        <div className={`mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          <div>
            <p className="font-medium">Auth Provider</p>
            <p>{profile.auth_provider || 'local'}</p>
          </div>
          <div>
            <p className="font-medium">Member Since</p>
            <p>{new Date(profile.created_at).toLocaleDateString()}</p>
          </div>
          <div>
            <p className="font-medium">Last Login</p>
            <p>{profile.last_login ? new Date(profile.last_login).toLocaleDateString() : '--'}</p>
          </div>
          <div>
            <p className="font-medium">Username</p>
            <p>{profile.username}</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-4 flex gap-3">
          {showRoleDropdown ? (
            <div className="flex items-center gap-2">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className={`px-3 py-1.5 border rounded-md text-sm ${
                  darkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="admin">Admin</option>
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
              <button
                onClick={handleRoleChange}
                disabled={changingRole}
                className={`px-3 py-1.5 rounded-md text-sm ${
                  darkMode
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                } ${changingRole ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {changingRole ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setShowRoleDropdown(false);
                  setSelectedRole(profile.role_name || profile.role || 'viewer');
                }}
                className={`px-3 py-1.5 rounded-md text-sm ${
                  darkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowRoleDropdown(true)}
              className={`px-3 py-1.5 rounded-md text-sm ${
                darkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              }`}
            >
              Change Role
            </button>
          )}
          <button
            onClick={() => setShowResetDialog(true)}
            className={`px-3 py-1.5 rounded-md text-sm ${
              darkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-white'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            }`}
          >
            Reset Password
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <nav className="flex px-6">
          <button
            onClick={() => setActiveTab('activity')}
            className={`py-3 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'activity'
                ? darkMode
                  ? 'border-blue-500 text-blue-400'
                  : 'border-blue-500 text-blue-600'
                : darkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Activity
          </button>
          <button
            onClick={() => setActiveTab('entities')}
            className={`py-3 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'entities'
                ? darkMode
                  ? 'border-blue-500 text-blue-400'
                  : 'border-blue-500 text-blue-600'
                : darkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Entities
          </button>
          <button
            onClick={() => setActiveTab('permissions')}
            className={`py-3 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'permissions'
                ? darkMode
                  ? 'border-blue-500 text-blue-400'
                  : 'border-blue-500 text-blue-600'
                : darkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Permissions
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div>
            {activityLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : activity.length === 0 ? (
              <p className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No activity recorded.
              </p>
            ) : (
              <div className="space-y-3">
                {activity.map((entry) => (
                  <div
                    key={entry.id}
                    className={`p-3 rounded-md border ${
                      darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {entry.action}
                        </p>
                        {entry.detail && (
                          <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {entry.detail}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {new Date(entry.timestamp).toLocaleString()}
                        </p>
                        {entry.ip_address && (
                          <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {entry.ip_address}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Entities Tab */}
        {activeTab === 'entities' && (
          <div>
            {entitiesLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : entities.length === 0 ? (
              <p className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No entities found.
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {entities.map((entity) => (
                  <div
                    key={entity.entity_type}
                    className={`p-4 rounded-lg border ${
                      darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <p className={`text-sm font-medium capitalize ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {entity.entity_type}
                    </p>
                    <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {entity.count}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Permissions Tab */}
        {activeTab === 'permissions' && profile?.permissions && (() => {
          const permGrid: Record<string, Set<string>> = {};
          const allActions = new Set<string>();
          for (const perm of profile.permissions) {
            const parsed = parsePermission(perm);
            if (!parsed) continue;
            if (!permGrid[parsed.entity]) permGrid[parsed.entity] = new Set();
            permGrid[parsed.entity].add(parsed.action);
            allActions.add(parsed.action);
          }
          const actionCols = ['read', 'write', 'delete', 'execute', 'admin'].filter(a => allActions.has(a));
          const entityRows = Object.keys(permGrid).sort();

          const toggleSort = (key: PermSortKey) => {
            if (permSortKey === key) setPermSortDir(d => d === 'asc' ? 'desc' : 'asc');
            else { setPermSortKey(key); setPermSortDir('asc'); }
          };
          const sortArrow = (key: PermSortKey) => {
            if (permSortKey !== key) return <span className="opacity-30 ml-0.5">{'\u2195'}</span>;
            return <span className="ml-0.5">{permSortDir === 'asc' ? '\u2191' : '\u2193'}</span>;
          };
          const sortedRows = [...entityRows].sort((a, b) => {
            let cmp = 0;
            if (permSortKey === 'entity') cmp = a.localeCompare(b);
            else {
              const aH = permGrid[a]?.has(permSortKey) ? 1 : 0;
              const bH = permGrid[b]?.has(permSortKey) ? 1 : 0;
              cmp = aH - bH;
            }
            return permSortDir === 'asc' ? cmp : -cmp;
          });

          const thClass = `px-3 py-2.5 text-[10px] uppercase tracking-wider font-semibold cursor-pointer select-none ${
            darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
          }`;

          return entityRows.length === 0 ? (
            <p className={`text-center py-8 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No permissions data.</p>
          ) : (
            <div className={`overflow-hidden rounded-lg ${darkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
              <table className="w-full">
                <thead>
                  <tr className={darkMode ? 'bg-gray-900/40' : 'bg-gray-100'}>
                    <th className={`${thClass} text-left px-4`} onClick={() => toggleSort('entity')}>Entity {sortArrow('entity')}</th>
                    {actionCols.map(a => (
                      <th key={a} className={`${thClass} text-center`} onClick={() => toggleSort(a as PermSortKey)}>{a} {sortArrow(a as PermSortKey)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-gray-700/30' : 'divide-gray-200/50'}`}>
                  {sortedRows.map(entity => {
                    const granted = actionCols.filter(a => permGrid[entity]?.has(a)).length;
                    return (
                      <tr key={entity} className={darkMode ? 'hover:bg-gray-700/20' : 'hover:bg-white'}>
                        <td className="px-4 py-2">
                          <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{entity}</div>
                          <div className={`text-[10px] ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>{granted}/{actionCols.length}</div>
                        </td>
                        {actionCols.map(action => (
                          <td key={action} className="px-3 py-2 text-center">
                            {permGrid[entity]?.has(action)
                              ? <span className={darkMode ? 'text-blue-400' : 'text-blue-600'}>&#10003;</span>
                              : <span className={darkMode ? 'text-gray-700' : 'text-gray-300'}>&mdash;</span>
                            }
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className={`px-4 py-2 border-t text-xs ${darkMode ? 'border-gray-700/30 text-gray-600' : 'border-gray-200/50 text-gray-400'}`}>
                {profile.permissions.length} permissions across {entityRows.length} entity types
              </div>
            </div>
          );
        })()}
      </div>

      {/* Reset Password Dialog */}
      {showResetDialog && profile && (
        <ResetPasswordDialog
          userId={profile.id}
          userName={profile.full_name || profile.username || profile.email}
          onClose={() => setShowResetDialog(false)}
        />
      )}
    </div>
  );
};

export default UserDetailPanel;
